import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import ShiftReportSection from './ShiftReportSection';
import { buildInitialFormData, createDefaultRow } from './shiftReportDefaults';
import AppShell from '../../components/layout/AppShell';
import { Button, PageHeader, Input, Select, Badge, Alert } from '../../components/ui';

const STATUS_OPTIONS = [
  { value: '', label: 'Select Status' },
  { value: 'OK', label: 'OK' },
  { value: 'N/A', label: 'N/A' }
];

const normalizeSrcTagSections = (value) => {
  if (value && typeof value === 'object') {
    const section = value.section;

    if (section && typeof section === 'object') {
      return Object.entries(section)
        .filter(([, areas]) => Array.isArray(areas))
        .map(([sectionName, areas]) => ({ section: sectionName, areas }));
    }
  }

  return [];
};

const normalizeAreaSections = (value) => {
  if (value && typeof value === 'object') {
    const section = value.section;

    if (section && typeof section === 'object') {
      return Object.entries(section)
        .filter(([, areas]) => Array.isArray(areas))
        .map(([sectionName, areas]) => ({
          section: sectionName,
          areas: areas
            .map((row) => (row && typeof row === 'object' ? row.area : ''))
            .filter(Boolean)
        }));
    }

    if (Array.isArray(value.areas)) {
      return [{ section: 'Areas', areas: value.areas.filter(Boolean) }];
    }
  }

  return [];
};

const toInputDate = (value) => {
  if (!value) {
    return '';
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
};

const hydrateReportIntoForm = (report) => {
  const base = buildInitialFormData();

  if (!report) {
    return base;
  }

  return {
    ...base,
    ...report,
    shift: report.shift ? String(report.shift) : '',
    date: toInputDate(report.date),
    secondDate: toInputDate(report.secondDate),
    scr: Array.isArray(report.scr) && report.scr.length > 0 ? report.scr : [createDefaultRow.scr()],
    microphoneStationGroup:
      Array.isArray(report.microphoneStationGroup) && report.microphoneStationGroup.length > 0
        ? report.microphoneStationGroup
        : [createDefaultRow.microphoneStationGroup()],
    correctiveMaintenance:
      Array.isArray(report.correctiveMaintenance) && report.correctiveMaintenance.length > 0
        ? report.correctiveMaintenance
        : [createDefaultRow.correctiveMaintenance()],
    systemFaultLog:
      Array.isArray(report.systemFaultLog) && report.systemFaultLog.length > 0
        ? report.systemFaultLog
        : [createDefaultRow.systemFaultLog()],
    ppm: Array.isArray(report.ppm) && report.ppm.length > 0 ? report.ppm : [createDefaultRow.ppm()],
    others: Array.isArray(report.others) && report.others.length > 0 ? report.others : [createDefaultRow.others()]
  };
};

function AddShiftReport() {
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const existingReport = location.state?.report || null;

  const [areas, setAreas] = useState([]);
  const [areaSections, setAreaSections] = useState([]);
  const [srcTagSections, setSrcTagSections] = useState([]);
  const [shiftTimeData, setShiftTimeData] = useState([]);
  const [formData, setFormData] = useState(() => hydrateReportIntoForm(existingReport));
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData(hydrateReportIntoForm(existingReport));
  }, [existingReport]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [areasRes, tagsRes, shiftRes] = await Promise.all([
          api.get('/api/areas', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/api/srcTags', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/api/shiftTime', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const normalizedAreas = normalizeAreaSections(areasRes.data);
        setAreaSections(normalizedAreas);
        setAreas(normalizedAreas.flatMap((section) => section.areas));
        setSrcTagSections(normalizeSrcTagSections(tagsRes.data));
        setShiftTimeData(Array.isArray(shiftRes.data?.ShiftTimeData) ? shiftRes.data.ShiftTimeData : []);
      } catch (error) {
        setErrorMessage('Failed to fetch metadata. Please refresh and try again.');
      }
    };

    fetchMetadata();
  }, [token]);

  const selectedScrSection = useMemo(
    () => srcTagSections.find((item) => item.section === formData.scrArea),
    [srcTagSections, formData.scrArea]
  );

  const tagsBySelectedArea = useMemo(() => {
    if (!selectedScrSection) {
      return [];
    }

    return selectedScrSection.areas.flatMap((areaBlock) =>
      Array.isArray(areaBlock.tags)
        ? areaBlock.tags.map((tag) => ({ ...tag, area: areaBlock.area }))
        : []
    );
  }, [selectedScrSection]);

  const groupedScrTagOptions = useMemo(() => {
    if (!selectedScrSection) {
      return [];
    }

    return selectedScrSection.areas
      .filter((areaBlock) => Array.isArray(areaBlock.tags) && areaBlock.tags.length > 0)
      .map((areaBlock) => ({
        label: areaBlock.area,
        options: areaBlock.tags.map((tag) => ({ value: tag.tag, label: tag.tag }))
      }));
  }, [selectedScrSection]);

  const handleMainChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === 'scrArea') {
        next.scr = [createDefaultRow.scr()];
      }

      if (name === 'shift') {
        const selectedShift = shiftTimeData.find((row) => String(row.ShiftNo) === value);
        next.shiftTime = selectedShift?.ShiftTime || '';
      }

      if (name === 'shift' || name === 'date') {
        if ((name === 'shift' ? value : next.shift) === '3' && (name === 'date' ? value : next.date)) {
          const d = new Date(name === 'date' ? value : next.date);
          d.setDate(d.getDate() + 1);
          next.secondDate = d.toISOString().split('T')[0];
        } else {
          next.secondDate = '';
        }
      }

      return next;
    });
  };

  const handleArrayChange = (event, rowIndex, sectionKey) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      const updatedRows = prev[sectionKey].map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        const updated = { ...row, [name]: value };
        if (sectionKey === 'scr' && name === 'tag') {
          const tag = tagsBySelectedArea.find((item) => item.tag === value);
          updated.number = tag?.number ?? '';
        }

        return updated;
      });

      return { ...prev, [sectionKey]: updatedRows };
    });
  };

  const addRow = (sectionKey) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: [...prev[sectionKey], createDefaultRow[sectionKey]()]
    }));
  };

  const removeRow = (sectionKey, rowIndex) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey].filter((_, index) => index !== rowIndex)
    }));
  };

  const buildPayload = () => {
    const payload = {
      ...formData,
      shift: Number(formData.shift),
      date: formData.date ? new Date(formData.date).toISOString() : '',
      secondDate: formData.secondDate ? new Date(formData.secondDate).toISOString() : ''
    };

    return payload;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const payload = buildPayload();
      const endpoint = existingReport
        ? `/shift-report/shift-reports/${existingReport._id}`
        : '/shift-report';
      const method = existingReport ? 'put' : 'post';

      const response = await api[method](endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage(existingReport ? 'Shift report updated successfully!' : 'Shift report added successfully!');

      if (existingReport) {
        navigate(`/view-shift-report/${existingReport._id}`);
      } else {
        navigate(`/view-shift-report/${response.data._id}`);
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 409 && data?.errorCode === 'SCR_TAG_CONFLICT') {
        const conflictingTags = Array.isArray(data?.conflictingTags)
          ? data.conflictingTags.filter(Boolean)
          : [];

        if (conflictingTags.length > 0) {
          setErrorMessage(`Cannot submit: SCR tags already used in another shift report for this date: ${conflictingTags.join(', ')}`);
        } else {
          setErrorMessage('Cannot submit: one or more SCR tags are already used in another shift report for this date.');
        }
      } else {
        setErrorMessage(data?.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrFields = [
    { name: 'time', label: 'Time', type: 'time' },
    {
      name: 'tag',
      label: 'Tag',
      type: 'select',
      options: [{ value: '', label: 'Select Tag' }],
      groupedOptions: groupedScrTagOptions
    },
    { name: 'number', label: 'Number', type: 'text' },
    { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
    { name: 'notes', label: 'Notes', type: 'textarea' }
  ];

  const areaOptions = [{ value: '', label: 'Select Area' }, ...areas.map((area) => ({ value: area, label: area }))];

  const groupedAreaOptions = useMemo(
    () => areaSections
      .filter((section) => Array.isArray(section.areas) && section.areas.length > 0)
      .map((section) => ({
        label: section.section,
        options: section.areas.map((area) => ({ value: area, label: area }))
      })),
    [areaSections]
  );

  const backTarget = existingReport ? `/view-shift-report/${existingReport._id}` : '/viewReports';

  return (
    <>
      <PageHeader
        title={existingReport ? 'Update shift report' : 'Add shift report'}
        subtitle="Log SCR activity, maintenance and system events for the shift"
        backTo={backTarget}
      />

      <AppShell>
        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-5xl">
          <section className="mb-4 rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-fg">
              Shift details
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              <Select
                label="Shift"
                name="shift"
                value={formData.shift}
                onChange={handleMainChange}
                required
              >
                <option value="">Select shift</option>
                {shiftTimeData.map((shift) => (
                  <option key={shift.ShiftNo} value={String(shift.ShiftNo)}>
                    {shift.ShiftNo}
                  </option>
                ))}
              </Select>

              <Input
                label="In charge"
                name="inCharge"
                value={formData.inCharge}
                onChange={handleMainChange}
                required
              />

              <Input
                label="Date"
                type="date"
                name="date"
                value={formData.date}
                onChange={handleMainChange}
                required
              />

              <Input
                label="Shift time"
                name="shiftTime"
                value={formData.shiftTime}
                readOnly
                disabled
                hint="Set automatically by the selected shift."
              />

              {formData.shift === '3' && formData.secondDate && (
                <Input
                  label="Second date"
                  type="date"
                  name="secondDate"
                  value={formData.secondDate}
                  readOnly
                  disabled
                  hint="Night shift spans two calendar days."
                />
              )}
            </div>
          </section>

          <section className="mb-4 overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
            <div className="flex items-center gap-3 px-4 py-3">
              <h2 className="text-sm font-semibold text-foreground">SCR</h2>
              <Badge variant="neutral" size="sm">{formData.scr.length}</Badge>
            </div>

            <div className="border-t border-border p-4">
              <Select
                label="Area"
                name="scrArea"
                value={formData.scrArea}
                onChange={handleMainChange}
                className="mb-4"
              >
                <option value="">Select section</option>
                {srcTagSections.map((section) => (
                  <option key={section.section} value={section.section}>
                    {section.section}
                  </option>
                ))}
              </Select>

              <ShiftReportSection
                title=""
                rows={formData.scr}
                fields={scrFields}
                sectionKey="scr"
                onRowChange={handleArrayChange}
                onAddRow={addRow}
                onRemoveRow={removeRow}
              />
            </div>
          </section>

        <ShiftReportSection
          title="Microphone Station Group"
          rows={formData.microphoneStationGroup}
          fields={[
            { name: 'time', label: 'Time', type: 'time' },
            {
              name: 'area',
              label: 'Area',
              type: 'select',
              options: [{ value: '', label: 'Select Area' }],
              groupedOptions: groupedAreaOptions
            },
            { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
          sectionKey="microphoneStationGroup"
          onRowChange={handleArrayChange}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />

        <ShiftReportSection
          title="Corrective Maintenance"
          rows={formData.correctiveMaintenance}
          fields={[
            { name: 'time', label: 'Time', type: 'time' },
            { name: 'cmNo', label: 'CM No', type: 'text' },
            { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
          sectionKey="correctiveMaintenance"
          onRowChange={handleArrayChange}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />

        <ShiftReportSection
          title="System Log"
          rows={formData.systemFaultLog}
          fields={[
            { name: 'time', label: 'Time', type: 'time' },
            { name: 'logType', label: 'Log Type', type: 'text' },
            { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
          sectionKey="systemFaultLog"
          onRowChange={handleArrayChange}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />

        <ShiftReportSection
          title="PPM"
          rows={formData.ppm}
          fields={[
            { name: 'time', label: 'Time', type: 'time' },
            { name: 'ppmNo', label: 'PPM No', type: 'text' },
            { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
          sectionKey="ppm"
          onRowChange={handleArrayChange}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />

        <ShiftReportSection
          title="Others"
          rows={formData.others}
          fields={[
            { name: 'time', label: 'Time', type: 'time' },
            { name: 'area', label: 'Area', type: 'select', options: areaOptions },
            { name: 'notes', label: 'Notes', type: 'textarea' }
          ]}
          sectionKey="others"
          onRowChange={handleArrayChange}
          onAddRow={addRow}
          onRemoveRow={removeRow}
        />

          {errorMessage && (
            <Alert variant="error" className="mt-6">
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert variant="success" className="mt-6">
              {successMessage}
            </Alert>
          )}

          {/* Sticks to the bottom on mobile so submit is reachable without
              scrolling past six repeatable sections. */}
          <div className="z-10 -mx-4 mt-6 flex gap-3 border-t border-border bg-surface/95 px-4 py-3 backdrop-blur sm:mx-0 sm:justify-end sm:border-0 sm:bg-transparent sm:px-0 sm:backdrop-blur-none">
            <Button type="button" variant="secondary" size="lg" onClick={() => navigate(backTarget)}>
              Cancel
            </Button>
            <Button type="submit" size="lg" loading={isSubmitting} className="flex-1 sm:flex-none">
              {isSubmitting ? 'Saving…' : existingReport ? 'Update report' : 'Submit report'}
            </Button>
          </div>
        </form>
      </AppShell>
    </>
  );
}

export default AddShiftReport;
